/**
 * klubb-campaign-tiles (D55, R2 release 4) — the home-page tile grid for the member's club.
 *
 * sections/klubb-tile-grid.liquid draws the tiles that are for everybody (the list picked in
 * Theme settings → Klubb · Forsidefliser), at most «Max tiles». Liquid cannot read the club cookie
 * (CLAUDE.md rule 4), so a member's OWN tiles (the Campaigns field on the club entry) are fetched
 * here: one request to templates/search.klubb-club-tiles.liquid with the club's slug, which answers
 * with that club's live tiles. They go FIRST in the grid, and the general tiles after them are
 * hidden as far as «Max tiles» needs, so nobody sees more tiles than the section allows.
 *
 * No club, a club without tiles, a request that fails or JavaScript off = the general tiles as the
 * server drew them. A failed request is not repeated until the club changes (no retry loop).
 * This is a visual feature: it may be briefly generic, and it never touches the cart or an order.
 *
 * Hooks: [data-klubb-campaign-section] (the section, carries data-klubb-campaign-max),
 * [data-klubb-campaign-items] (the grid), [data-klubb-campaign] (a tile) and
 * [data-klubb-campaign-own] (a tile fetched for the member's club).
 *
 * Exports the pure helpers for unit tests; auto-inits in the browser.
 */

// Keep in step with templates/search.klubb-club-tiles.liquid (the same shape as klubb-club-state.js FEED_URL).
const TILES_URL = '/search?view=klubb-club-tiles&q=';

function norm(slug) {
  return String(slug == null ? '' : slug).trim().toLowerCase();
}

// Where to ask for a club's tiles; '' when there is no club.
export function tilesUrl(slug) {
  const s = norm(slug);
  return s === '' ? '' : TILES_URL + encodeURIComponent(s);
}

// How many of each kind of tile show: the member's own come first, the general ones fill what is
// left under `max` (not a positive number = no limit).
export function tileCounts(ownCount, generalCount, max) {
  const limit = Number(max) > 0 ? Number(max) : Infinity;
  const own = Math.min(Math.max(Number(ownCount) || 0, 0), limit);
  const general = Math.min(Math.max(Number(generalCount) || 0, 0), limit - own);
  return { own: own, general: general };
}

function parseTiles(html) {
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  return Array.prototype.map.call(doc.querySelectorAll('[data-klubb-campaign]'), function (node) {
    const tile = document.importNode(node, true);
    tile.setAttribute('data-klubb-campaign-own', '');
    return tile;
  });
}

function wireSection(klubb, section) {
  const items = section.querySelector('[data-klubb-campaign-items]');
  if (!items) return;
  const max = section.getAttribute('data-klubb-campaign-max');
  let own = [];      // the member's tiles now in the grid
  let ownFor = '';   // the club slug they were fetched for ('' = none asked for)

  function place() {
    const general = Array.prototype.slice.call(items.querySelectorAll('[data-klubb-campaign]:not([data-klubb-campaign-own])'));
    const counts = tileCounts(own.length, general.length, max);
    own.forEach(function (t, i) { t.hidden = i >= counts.own; });
    general.forEach(function (t, i) { t.hidden = i >= counts.general; });
    section.hidden = counts.own + counts.general === 0;
  }

  function setOwn(tiles) {
    own.forEach(function (t) { t.remove(); });
    own = tiles;
    const first = items.firstChild;
    tiles.forEach(function (t) { items.insertBefore(t, first); });
    place();
  }

  function sync() {
    // pendingSlug: the cookie names a club the browser has not looked up yet; the tiles only need its slug.
    const club = klubb.activeClub;
    const slug = norm(club ? club.slug : klubb.pendingSlug);
    if (slug === ownFor) return;
    ownFor = slug;
    if (slug === '') { setOwn([]); return; }
    fetch(tilesUrl(slug), { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (ownFor !== slug) return; // the member changed club while this was on its way
        setOwn(parseTiles(html));
      })
      .catch(function () { /* the general tiles stand */ });
  }

  place();
  sync();
  if (typeof klubb.on === 'function') klubb.on('change', sync);
}

function init() {
  const klubb = (typeof window !== 'undefined') ? window.klubb : null;
  if (!klubb) return;
  document.querySelectorAll('[data-klubb-campaign-section]').forEach(function (section) { wireSection(klubb, section); });
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
